import React, { useRef, useState } from 'react';
import { useSelector } from "react-redux";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import DynamicSimpleTable from "../../../util/DynamicSimpleReactTable";
import {
    errorMessage,
    formatAmount,
    formatDate,
    formatStatus,
    formatUsername,
    permissionAllow,
    permissionCssClass
} from "../../../util/Helpers";
import cogoToast from "cogo-toast";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import ViewPaymentModal
    from "../../UserManagement/pages/selected-user/view-user/user-pages/account-information/account-pages/payments/modal/ViewPaymentModal";
import { Button, Modal } from "react-bootstrap";
import { Permissions, PermissionType } from "../../../util/Permissions";
import EditPaymentModal from "./modals/EditPaymentModal";
import { FilterGroup } from "../../Common/components/FilterGroup"
import { useApproveDepositMutation, useApproveWithdrawalMutation, useSubmitPaymentsHistoryExportMutation } from "../../../../redux/apiSlice";
import { Permission } from "../../Common/layout/Permission"

const getExportErrorMessage = (error) => (
    error?.data
    || error?.error
    || error?.message
    || errorMessage(error)
    || "Payments export failed"
);

export const PaymentTable = ({ userGroup, category }) => {
    const adminUser = useSelector(state => state.auth.user);
    const defaultType = category === "virtual-card-creation"
        ? "CARD_CREATION"
        : category === "virtual-card-deposit"
            ? "CARD_DEPOSIT"
            : "";
    const requestCategory = ["virtual-card-creation", "virtual-card-deposit"].includes(category)
        ? ""
        : category;
    /* Payment Modal State Management */
    const [paymentModal, setPaymentModal] = useState({
        showViewPaymentModal: false,
        showEditPaymentModal: false,
        details: null
    });

    const [submitPaymentsHistoryExport] = useSubmitPaymentsHistoryExportMutation();
    const [isExporting, setIsExporting] = useState(false);

    const [approveDeposit] = useApproveDepositMutation();
    const [approveWithdrawal] = useApproveWithdrawalMutation();

    const tableRef = useRef();
    const [selectedPayments, setSelectedPayments] = useState({});
    const visibleSelectablePaymentsRef = useRef({});
    const [bulkApproval, setBulkApproval] = useState({
        inProgress: false,
        current: 0,
        total: 0,
        success: 0,
        failed: 0,
        skipped: 0,
    });
    const [recentBulkResults, setRecentBulkResults] = useState([]);
    const [bulkConfirm, setBulkConfirm] = useState({
        show: false,
        action: "approve",
    });

    const formatActivity = row => {
        return <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>

            <span className={`svg-icon svg-icon-lg svg-icon-${row.type === "DEPOSIT" ? "primary" : "danger"} mr-3`}>
                <SVG
                    title=""
                    src={toAbsoluteUrl("/media/svg/icons/Shopping/Gift.svg")}
                />
            </span>
            <div>
                <span className=" mb-1 font-size-lg">
                    {row.type}
                    <span className="text- pl-2">
                        / {row.method ? ` ${row.method}` : "***"}
                    </span>
                    <span className="text- pl-2">
                        / {row.last4 ? ` ****${row.last4}` : "***"}
                    </span>
                </span>
            </div>
        </div>
    }

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setPaymentModal({ ...paymentModal, [modal]: !paymentModal[modal] })
    }

    const viewPayment = details => {
        setPaymentModal({ ...paymentModal, showViewPaymentModal: true, details });
    }

    /* Edit Payment Modal*/
    const editPayment = details => {
        setPaymentModal({
            ...paymentModal,
            showEditPaymentModal: true,
            details
        })
    }

    const canApprove = row => (
        (
            (row.status === "PENDING_CONFIRMATION" && row.type === "WITHDRAW") ||
            (row.status === "PROCESSING" && row.type === "DEPOSIT") ||
            (row.status === "PROCESSING" && row.type === "CARD_CREATION") ||
            (row.status === "PROCESSING" && row.type === "CARD_DEPOSIT") ||
            (row.status === "PROCESSING" && row.type === "PORTFOLIO_CREATION")
        ) &&
        permissionAllow(adminUser, Permissions.PAYMENTS, PermissionType.UPDATE)
    );

    const isSelectable = row => {
        const status = (row.status || "").toUpperCase();
        return status !== "COMPLETED" && status !== "FAILED";
    };

    const toggleSelectedPayment = row => {
        setSelectedPayments(prev => {
            if (!prev[row.ref] && Object.keys(prev).length >= 20) {
                cogoToast.warn("You can select up to 20 payments for bulk approval.");
                return prev;
            }
            const next = { ...prev };
            if (next[row.ref]) {
                delete next[row.ref];
            } else {
                next[row.ref] = row;
            }
            return next;
        });
    };

    const handleSelectAllVisible = () => {
        if (bulkApproval.inProgress) {
            return;
        }
        const visiblePayments = Object.values(visibleSelectablePaymentsRef.current || {}).filter(isSelectable);
        if (visiblePayments.length === 0) {
            return;
        }
        setSelectedPayments(prev => {
            const next = { ...prev };
            for (const payment of visiblePayments) {
                if (next[payment.ref]) {
                    continue;
                }
                if (Object.keys(next).length >= 20) {
                    cogoToast.warn("You can select up to 20 payments for bulk approval.");
                    break;
                }
                next[payment.ref] = payment;
            }
            return next;
        });
    };

    const appendBulkResult = entry => {
        setRecentBulkResults(prev => [entry, ...prev].slice(0, 50));
    };

    const handleBulkApprove = async payments => {
        if (payments.length === 0) {
            return;
        }
        setBulkApproval({
            inProgress: true,
            current: 0,
            total: payments.length,
            success: 0,
            failed: 0,
            skipped: 0,
        });
        let success = 0;
        let failed = 0;
        let skipped = 0;
        let current = 0;
        for (const payment of payments) {
            current += 1;
            setBulkApproval(prev => ({ ...prev, current }));
            if (!canApprove(payment)) {
                skipped += 1;
                setBulkApproval(prev => ({
                    ...prev,
                    current,
                    skipped,
                }));
                appendBulkResult({
                    ref: payment.ref,
                    result: "FAILED",
                    at: new Date().toISOString(),
                });
                continue;
            }
            try {
                const data = {
                    ref: payment.ref,
                    userId: payment.userId,
                    meta: {
                        progress: payment.progress,
                        updatedAt: payment.updatedAt,
                        status: payment.status,
                    },
                };
                if (payment.type === "WITHDRAW") {
                    await approveWithdrawal(data).unwrap();
                } else {
                    await approveDeposit(data).unwrap();
                }
                success += 1;
                appendBulkResult({
                    ref: payment.ref,
                    result: "COMPLETED",
                    at: new Date().toISOString(),
                });
            } catch (error) {
                failed += 1;
                appendBulkResult({
                    ref: payment.ref,
                    result: "FAILED",
                    at: new Date().toISOString(),
                });
            }
            setBulkApproval(prev => ({
                ...prev,
                current,
                success,
                failed,
                skipped,
            }));
        }
        cogoToast.success(`Bulk approval completed: ${success} completed, ${failed} failed, ${skipped} skipped.`);
        setSelectedPayments({});
        setBulkApproval(prev => ({ ...prev, inProgress: false }));
    };

    const openBulkConfirm = action => {
        const payments = Object.values(selectedPayments);
        if (payments.length === 0) {
            return;
        }
        setBulkConfirm({
            show: true,
            action,
        });
    };

    const buildPaymentsExportPayload = (filters = {}, format = "csv") => {
        const payload = {
            userGroup,
            type: filters?.type || "",
            status: filters?.status || "",
            startDate: filters?.startDate || "",
            endDate: filters?.endDate || "",
            dateKey: "created",
            order: "desc",
            format,
        };

        return Object.entries(payload).reduce((accumulator, [key, value]) => {
            if (value === undefined || value === null || `${value}`.trim() === "") {
                return accumulator;
            }

            accumulator[key] = value;
            return accumulator;
        }, {});
    };

    const handlePaymentsExport = async (filters = {}, format = "csv") => {
        try {
            setIsExporting(true);
            cogoToast.info(`Submitting ${format.toUpperCase()} payments export...`);

            await submitPaymentsHistoryExport(
                buildPaymentsExportPayload(filters, format)
            ).unwrap();

            cogoToast.success(`Payments ${format.toUpperCase()} report will be sent to your email.`);
        } catch (error) {
            cogoToast.error(getExportErrorMessage(error));
        } finally {
            setIsExporting(false);
        }
    };

    /* Action Buttons */
    const paymentAction = (row) => {
        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <Button
                    title="View Payment"
                    className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                    onClick={_ => viewPayment(row)}
                >
                    <span className="svg-icon svg-icon-md svg-icon-primary">
                        {
                            canApprove(row)
                                ? <SVG
                                    title="Approve Payment"
                                    src={toAbsoluteUrl("/media/svg/icons/Media/Play.svg")}
                                />
                                : <SVG
                                    title="View Payment"
                                    src={toAbsoluteUrl(
                                        "/media/svg/icons/General/Eye-2.svg"
                                    )}
                                />
                        }
                    </span>
                </Button>

                <Button
                    title="Edit Payment"
                    className={`btn btn-icon btn-light btn-hover-primary btn-sm mx-3 ${permissionCssClass(adminUser, Permissions.PAYMENTS, PermissionType.UPDATE)}`}
                    onClick={_ => editPayment(row)}

                >
                    <span className="svg-icon svg-icon-md svg-icon-primary">
                        <SVG
                            title="Edit Payment"
                            src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")}
                        />
                    </span>
                </Button>
            </span>
        );
    };

    return (
        <>
            <FilterGroup
                defaultFilters={{
                    endDate: new Date().toISOString().split('T')[0],
                    type: defaultType,
                    category: requestCategory,
                    status: "",
                }}
                key={category}
            >
                <FilterGroup.DateRange className={"mb-4"} />
                <div className="d-flex">
                    <div className={"mr-4"}>
                        <FilterGroup.Filter name={"limit"} label={"Limit"}>
                            <FilterGroup.Option value={""}>10</FilterGroup.Option>
                            <FilterGroup.Option value="20">20</FilterGroup.Option>
                            <FilterGroup.Option value="50">50</FilterGroup.Option>
                            <FilterGroup.Option value="100">100</FilterGroup.Option>
                        </FilterGroup.Filter>
                    </div>
                    {
                        category === "all" &&
                        <>
                            <div className={"mr-4"}>
                                <FilterGroup.Filter name={"type"} label={"payment type"}>
                                    <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                    <FilterGroup.Option value={"DEPOSIT"}>Deposits</FilterGroup.Option>
                                    <FilterGroup.Option value={"WITHDRAW"}>Withdrawals</FilterGroup.Option>
                                    <FilterGroup.Option value={"PORTFOLIO_CREATION"}>Portfolio Creations</FilterGroup.Option>
                                    <FilterGroup.Option value={"CARD_CREATION"}>Virtual Card Creations</FilterGroup.Option>
                                    <FilterGroup.Option value={"CARD_DEPOSIT"}>Virtual Card Deposits</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            <div className={"mr-4"}>
                                <FilterGroup.Filter name={"status"} label={"status"}>
                                    <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                    <FilterGroup.Option value={"COMPLETED"}>COMPLETED</FilterGroup.Option>
                                    <FilterGroup.Option value={"FAILED"}>FAILED</FilterGroup.Option>
                                    <FilterGroup.Option value={"PENDING"}>PENDING</FilterGroup.Option>
                                    <FilterGroup.Option value={"PROCESSING"}>PROCESSING</FilterGroup.Option>
                                    <FilterGroup.Option value={"PENDING_CONFIRMATION"}>PENDING_CONFIRMATION</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                        </>
                    }
                </div>
                <FilterGroup.Content className="mt-5">
                    {
                        ({ filters }) => {
                            const selectedCount = Object.keys(selectedPayments).length;
                            visibleSelectablePaymentsRef.current = {};
                            return <Card>
                                <CardHeader title={`All ${filters?.type?.toLowerCase() || "payment"}s`}>
                                    <CardHeaderToolbar>
                                        <Permission permission={Permissions.PAYMENTS} permissionType={PermissionType.UPDATE}>
                                            <Button
                                                type="button"
                                                className="btn btn-primary mr-4"
                                                disabled={selectedCount === 0 || bulkApproval.inProgress}
                                                onClick={() => openBulkConfirm("approve")}
                                            >
                                                {bulkApproval.inProgress
                                                    ? `Approving ${bulkApproval.current}/${bulkApproval.total}`
                                                    : `Bulk Approve${selectedCount ? ` (${selectedCount})` : ""}`}
                                            </Button>
                                            <Button
                                                type="button"
                                                className="btn btn-light mr-4"
                                                onClick={handleSelectAllVisible}
                                                disabled={bulkApproval.inProgress}
                                            >
                                                Select All
                                            </Button>
                                        </Permission>
                                        <Permission permission={Permissions.REPORTS} permissionType={PermissionType.CREATE}>
                                            <>
                                                {/*
                                                <button
                                                    type="button"
                                                    className="btn btn-primary mr-4"
                                                    disabled={isExporting}
                                                    onClick={() => downloadPaymentsReport({
                                                        userGroup,
                                                        startDate: filters?.startDate || "",
                                                        endDate: filters?.endDate || "",
                                                        type: filters?.type || "",
                                                        status: filters?.status || "",
                                                        category: filters?.category || requestCategory,
                                                        format: "xlsx",
                                                    })}
                                                >
                                                    Export XLSX
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    disabled={isExporting}
                                                    onClick={() => downloadPaymentsReport({
                                                        userGroup,
                                                        startDate: filters?.startDate || "",
                                                        endDate: filters?.endDate || "",
                                                        type: filters?.type || "",
                                                        status: filters?.status || "",
                                                        category: filters?.category || requestCategory,
                                                        format: "csv",
                                                    })}
                                                >
                                                    Export CSV
                                                </button>
                                                */}
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    disabled={isExporting}
                                                    onClick={() => handlePaymentsExport(filters, "csv")}
                                                >
                                                    {isExporting ? "Submitting..." : "Export Payments"}
                                                </button>
                                            </>
                                        </Permission>
                                    </CardHeaderToolbar>
                                </CardHeader>
                                <CardBody>
                                    <div className="mb-4">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <h5 className="mb-0">Recent bulk results</h5>
                                            <button
                                                type="button"
                                                className="btn btn-light btn-sm"
                                                disabled={bulkApproval.inProgress || recentBulkResults.length === 0}
                                                onClick={() => setRecentBulkResults([])}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-borderless mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>REFERENCE</th>
                                                        <th>RESULT</th>
                                                        <th>TIME</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {recentBulkResults.length > 0 ? (
                                                        recentBulkResults.map((entry, index) => (
                                                            <tr key={`${entry.ref}-${entry.at}-${index}`}>
                                                                <td className="text-monospace">{entry.ref}</td>
                                                                <td>{formatStatus(entry.result)}</td>
                                                                <td>{formatDate(entry.at)}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="3" className="text-muted text-center py-3">
                                                                No recent bulk results yet.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <DynamicSimpleTable
                                        key={`${userGroup}-${category}`}
                                        queryParams={
                                            `userGroup=${userGroup}`
                                            + `&type=${filters?.type || ""}`
                                            + `&status=${filters?.status || ""}`
                                            + `&limit=${filters?.limit || 10}`
                                            + `&startDate=${filters?.startDate || ""}`
                                            + `&endDate=${filters?.endDate || ""}`
                                            + `&category=${filters?.category || requestCategory}`
                                        }
                                        searchBox={true}
                                        source={{ url: `/payments` }}
                                        columns={[
                                            {
                                                title: "SELECT",
                                                format: row => {
                                                    if (!isSelectable(row)) {
                                                        return <span className="text-muted">-</span>;
                                                    }
                                                    visibleSelectablePaymentsRef.current[row.ref] = row;
                                                    return (
                                                        <label className="checkbox checkbox-single mb-0">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!selectedPayments[row.ref]}
                                                                onChange={() => toggleSelectedPayment(row)}
                                                                disabled={bulkApproval.inProgress}
                                                            />
                                                            <span />
                                                        </label>
                                                    );
                                                }
                                            },
                                            { title: "ACTIVITY", format: formatActivity },
                                            { title: "REFERENCE", key: "ref" },
                                            { title: "USER", format: row => formatUsername(row.userId, row.username, null, row.accountId, "payments") },
                                            { title: "CURRENCY", key: "currency" },
                                            { title: "AMOUNT", format: row => formatAmount(row.currency, row.amount) },
                                            { title: "CREATED ON", format: (row) => formatDate(row.createdAt) },
                                            { title: "STATUS", format: row => formatStatus(row.status) },
                                            { title: "", format: paymentAction }
                                        ]}
                                        ref={tableRef}
                                        onError={(err) =>
                                            cogoToast.error(errorMessage(err))
                                        }
                                        refreshIcon={true}
                                    />
                                </CardBody>
                            </Card>
                        }
                    }
                </FilterGroup.Content>
            </FilterGroup>

            {
                paymentModal.showViewPaymentModal &&
                <ViewPaymentModal
                    {...paymentModal}
                    tableRef={tableRef}
                    onHide={toggleModal("showViewPaymentModal")}
                />
            }

            {
                paymentModal.showEditPaymentModal &&
                <EditPaymentModal
                    {...paymentModal}
                    tableRef={tableRef}
                    onHide={toggleModal("showEditPaymentModal")}
                />
            }

            <Modal
                show={bulkConfirm.show}
                onHide={() => setBulkConfirm(prev => ({ ...prev, show: false }))}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {bulkConfirm.action === "decline"
                            ? "Confirm Bulk Decline"
                            : "Confirm Bulk Approval"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {bulkConfirm.action === "decline"
                            ? "Are you sure you want to decline these transactions?"
                            : "Are you sure you want to approve these transactions?"}
                    </p>
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>USER</th>
                                    <th>AMOUNT</th>
                                    <th>TYPE</th>
                                    <th>REFERENCE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.values(selectedPayments).map((payment, index) => (
                                    <tr key={`${payment.ref}-${index}`}>
                                        <td>
                                            {formatUsername(
                                                payment.userId,
                                                payment.username,
                                                null,
                                                payment.accountId,
                                                "payments"
                                            )}
                                        </td>
                                        <td>{formatAmount(payment.currency, payment.amount)}</td>
                                        <td>{payment.type}</td>
                                        <td className="text-monospace">{payment.ref}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-light"
                        disabled={bulkApproval.inProgress}
                        onClick={() => setBulkConfirm(prev => ({ ...prev, show: false }))}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`btn ${bulkConfirm.action === "decline" ? "btn-danger" : "btn-primary"}`}
                        disabled={bulkApproval.inProgress}
                        onClick={async () => {
                            const payments = Object.values(selectedPayments);
                            setBulkConfirm(prev => ({ ...prev, show: false }));
                            await handleBulkApprove(payments);
                        }}
                    >
                        {bulkConfirm.action === "decline" ? "Decline" : "Approve"}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
